import React, { useState, useEffect } from "react";
import { Dropzone } from "@files-ui/react";
import UploadButton from "./UploadButton";
import BankSelector from "./BankSelector";
import AlertMessage from "./AlertMessage";
import UploadProgress from "./UploadProgress";
import { API } from "../constants";
import Preview from "./Preview";
import SystemReadinessBadge from "./SystemReadinessBadge";
import ProductChoice from "./ProductChoice";
import ClientDetailsPayment from "./ClientDetailsPayment";
import BillingPage from "./BillingPage";
import ResultsPage from "./ResultsPage";
import { Typography, Box, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { computeIsGermanDefault } from '../utils/language';

function PayrexxCheckoutSimulator() {
    const [method, setMethod] = React.useState('twint'); // twint | cards
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [twintPhone, setTwintPhone] = React.useState('');
    const [twintReference, setTwintReference] = React.useState('');
    const [cardNumber, setCardNumber] = React.useState('');
    const [cardExpiry, setCardExpiry] = React.useState('');
    const [cardCvc, setCardCvc] = React.useState('');

    const formatTwintPhone = (raw) => {
        const hasPlus = String(raw || '').trim().startsWith('+');
        const digits = String(raw || '').replace(/\D/g, '');

        // CHF heuristic: CH country code 41 + format: +41 79 123 45 67
        if (digits.startsWith('41')) {
            const rest = digits.slice(2);
            const area = rest.slice(0, 2);
            const g1 = rest.slice(2, 5);
            const g2 = rest.slice(5, 7);
            const g3 = rest.slice(7, 9);

            let out = '41';
            if (area) out += ' ' + area;
            if (g1) out += ' ' + g1;
            if (g2) out += ' ' + g2;
            if (g3) out += ' ' + g3;
            return (hasPlus ? '+' : '') + out;
        }

        // Generic: group into 3-digit chunks, keep partial.
        const parts = digits.match(/.{1,3}/g) || [];
        return (hasPlus ? '+' : '') + parts.join(' ');
    };

    const formatCardNumber = (raw) => {
        const digits = String(raw || '').replace(/\D/g, '').slice(0, 19);
        const groups = digits.match(/.{1,4}/g) || [];
        return groups.join(' ');
    };

    const formatCardExpiry = (raw) => {
        const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
        if (digits.length <= 2) return digits;
        return digits.slice(0, 2) + '/' + digits.slice(2);
    };

    const formatCardCvc = (raw) => {
        return String(raw || '').replace(/\D/g, '').slice(0, 4);
    };

    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search || '') : new URLSearchParams();
    const lang = params.get('lang') || '';
    const orderIdRaw = params.get('order_id') || (typeof window !== 'undefined' ? localStorage.getItem('lastOrderId') : '') || '';
    const orderId = parseInt(orderIdRaw, 10);

    const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '::1'
    );
    const simUrl = origin + '/?rest_route=/ryp/v1/payrexx-simulate-webhook';

    const redirectToBilling = () => {
        const search = new URLSearchParams();
        search.set('page_id', '63');
        search.set('resume_checkout', '1');
        if (lang) search.set('lang', lang);
        window.location.href = origin + '/?' + search.toString();
    };

    const callScenario = async (scenario, eventId) => {
        const body = {
            order_id: orderId,
            scenario: scenario,
            event_id: eventId
        };

        const res = await fetch(simUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
            throw new Error((data && data.message) ? data.message : 'Simulator call failed');
        }
        return data;
    };

    const simulateProcessingThenPaid = async () => {
        if (!isLocalhost) {
            setError('Payrexx checkout simulator is enabled for localhost only.');
            return;
        }
        const validationError = (() => {
            if (!Number.isFinite(orderId) || orderId <= 0) return 'Missing order_id. Please create a Payrexx order first.';
            if (method === 'twint') {
                const phone = (twintPhone || '').trim();
                if (phone.length < 5) return 'Please enter a phone number for TWINT.';
                return '';
            }
            // PostFinance / Cards
            const cn = (cardNumber || '').replace(/\s+/g, '');
            if (!/^\d{12,19}$/.test(cn)) return 'Please enter a valid card number (digits only).';
            const ex = (cardExpiry || '').trim();
            if (!/^\d{2}\/\d{2}$/.test(ex)) return 'Please enter expiry as MM/YY.';
            const cvc = (cardCvc || '').replace(/\s+/g, '');
            if (!/^\d{3,4}$/.test(cvc)) return 'Please enter CVC (3-4 digits).';
            return '';
        })();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setBusy(true);
        try {
            // 1) pending (TWINT/cards in Payrexx)
            await callScenario('pending', `SIM-${orderId}-PENDING-${method}-${Date.now()}`);
            // 2) confirmed/paid
            setTimeout(() => {}, 1); // keep UI thread stable; processing happens below
            await new Promise((r) => setTimeout(r, 1500));
            await callScenario('paid', `SIM-${orderId}-PAID-${method}-${Date.now()}`);
            redirectToBilling();
        } catch (e) {
            setError(e && e.message ? e.message : 'Simulator error');
            setBusy(false);
        }
    };

    const simulateSingle = async (scenario, label) => {
        if (!isLocalhost) {
            setError('Payrexx checkout simulator is enabled for localhost only.');
            return;
        }
        const validationError = (() => {
            if (!Number.isFinite(orderId) || orderId <= 0) return 'Missing order_id. Please create a Payrexx order first.';
            if (method === 'twint') {
                const phone = (twintPhone || '').trim();
                if (phone.length < 5) return 'Please enter a phone number for TWINT.';
                return '';
            }
            const cn = (cardNumber || '').replace(/\s+/g, '');
            if (!/^\d{12,19}$/.test(cn)) return 'Please enter a valid card number (digits only).';
            const ex = (cardExpiry || '').trim();
            if (!/^\d{2}\/\d{2}$/.test(ex)) return 'Please enter expiry as MM/YY.';
            const cvc = (cardCvc || '').replace(/\s+/g, '');
            if (!/^\d{3,4}$/.test(cvc)) return 'Please enter CVC (3-4 digits).';
            return '';
        })();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setBusy(true);
        try {
            await callScenario(scenario, `SIM-${orderId}-${scenario}-${Date.now()}-${label}`);
            redirectToBilling();
        } catch (e) {
            setError(e && e.message ? e.message : 'Simulator error');
            setBusy(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 720, margin: '28px auto', padding: 3 }}>
            <Typography variant="h4" align="center" sx={{ mb: 2 }}>Payrexx Checkout (Local Simulator)</Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Order #{Number.isFinite(orderId) ? orderId : 'unknown'} — simulate TWINT or PostFinance/cards
            </Typography>

            {error && (
                <Box sx={{ mb: 2, padding: 2, border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 2 }}>
                    <Typography variant="body2" color="error">{error}</Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        type="radio"
                        name="method"
                        value="twint"
                        checked={method === 'twint'}
                        onChange={() => setMethod('twint')}
                        disabled={busy}
                    />
                    TWINT
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                        type="radio"
                        name="method"
                        value="cards"
                        checked={method === 'cards'}
                        onChange={() => setMethod('cards')}
                        disabled={busy}
                    />
                    PostFinance / Cards
                </label>
            </Box>

            {/* Method-specific fields (UI parity; status is still driven by backend simulator scenario). */}
            {method === 'twint' ? (
                <Box sx={{ mb: 3, padding: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>TWINT details (simulated)</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <div>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Phone number</Typography>
                            <input
                                value={twintPhone}
                                onChange={(e) => setTwintPhone(formatTwintPhone(e.target.value))}
                                placeholder="+41 79 123 45 67"
                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                                disabled={busy}
                            />
                        </div>
                        <div>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Optional reference</Typography>
                            <input
                                value={twintReference}
                                onChange={(e) => setTwintReference(e.target.value)}
                                placeholder="Reference (optional)"
                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                                disabled={busy}
                            />
                        </div>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ mb: 3, padding: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>PostFinance / Cards details (simulated)</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <div>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Card number</Typography>
                            <input
                                value={cardNumber}
                                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                placeholder="4242 4242 4242 4242"
                                inputMode="numeric"
                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                                disabled={busy}
                            />
                        </div>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <div>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Expiry (MM/YY)</Typography>
                                <input
                                    value={cardExpiry}
                                    onChange={(e) => setCardExpiry(formatCardExpiry(e.target.value))}
                                    placeholder="12/34"
                                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                                    disabled={busy}
                                />
                            </div>
                            <div>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>CVC</Typography>
                                <input
                                    value={cardCvc}
                                    onChange={(e) => setCardCvc(formatCardCvc(e.target.value))}
                                    placeholder="123"
                                    inputMode="numeric"
                                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                                    disabled={busy}
                                />
                            </div>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                    onClick={simulateProcessingThenPaid}
                    disabled={busy}
                    style={{
                        padding: '12px 18px',
                        borderRadius: 8,
                        border: '1px solid #0d6efd',
                        background: '#0d6efd',
                        color: '#fff',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        minWidth: 220,
                        fontSize: 14
                    }}
                >
                    {busy ? 'Processing...' : `Pay (simulate ${method === 'twint' ? 'TWINT' : 'PostFinance/cards'} pending → paid)`}
                </button>

                <button
                    onClick={() => simulateSingle('failed', 'failed')}
                    disabled={busy}
                    style={{
                        padding: '12px 18px',
                        borderRadius: 8,
                        border: '1px solid #dc3545',
                        background: '#dc3545',
                        color: '#fff',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        minWidth: 160,
                        fontSize: 14
                    }}
                >
                    Fail
                </button>

                <button
                    onClick={() => simulateSingle('cancelled', 'cancelled')}
                    disabled={busy}
                    style={{
                        padding: '12px 18px',
                        borderRadius: 8,
                        border: '1px solid #6c757d',
                        background: '#6c757d',
                        color: '#fff',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        minWidth: 160,
                        fontSize: 14
                    }}
                >
                    Cancel
                </button>

                <button
                    onClick={() => simulateSingle('currency_mismatch', 'mismatch')}
                    disabled={busy}
                    style={{
                        padding: '12px 18px',
                        borderRadius: 8,
                        border: '1px solid #ff9800',
                        background: '#ff9800',
                        color: '#111',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        minWidth: 160,
                        fontSize: 14
                    }}
                >
                    Amount/Currency mismatch
                </button>
            </Box>
        </Box>
    );
}

function isUiTextTestMode() {
    try {
        const params = new URLSearchParams(window.location.search || '');
        return params.get('ui_text_test') === '1';
    } catch (e) {
        return false;
    }
}

function getCurrentLangCode() {
    try {
        const params = new URLSearchParams(window.location.search || '');
        const q = (params.get('lang') || '').toLowerCase();
        if (q === 'en' || q === 'de') return q;
        if (q === 'pl') return 'pl';
        const remembered = (localStorage.getItem('bmn_preferred_lang') || '').toLowerCase();
        if (remembered === 'en' || remembered === 'de' || remembered === 'pl') return remembered;
        const saved = (localStorage.getItem('ryp_lang') || '').toLowerCase();
        if (saved === 'en' || saved === 'de') return saved;
        if (saved === 'pl') return 'pl';
    } catch (e) {}
    return 'de';
}

function getUiTextRestUrl(lang, page, mode, includeTestFlag) {
    const origin = window.location.origin || '';
    let url =
        origin +
        '/index.php?rest_route=/ryp/v1/ui-texts' +
        '&lang=' + encodeURIComponent(lang) +
        '&page=' + encodeURIComponent(page) +
        '&status=' + encodeURIComponent(mode || 'approved');
    if (includeTestFlag) url += '&ui_text_test=1';
    return url;
}

function getUploadText(texts, key, fallback) {
    const v = texts && texts[key];
    if (v === undefined || v === null || v === '') return fallback;
    return String(v).trim();
}

function UploadFile() {
    const MAX_FILES = 10;
    const [banks, setBanks] = useState([]); // List of banks
    const [banksLoadState, setBanksLoadState] = useState({ status: "idle", message: "" }); // idle | loading | ready | issue
    const [selectedBank, setSelectedBank] = useState(() => {
        // Get from localStorage or default
        return localStorage.getItem('selectedBank') || "Degiro";
    });
    const [files, setFiles] = useState([]); // Dropzone files
    const [isGerman, setIsGerman] = useState(() => computeIsGermanDefault());
    const isPolish = getCurrentLangCode() === 'pl';
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [uiTexts, setUiTexts] = useState({});
    const [sampleBusy, setSampleBusy] = useState(false);
    const [sampleError, setSampleError] = useState('');
    
    // Store selected bank in localStorage when it changes
    useEffect(() => {
        localStorage.setItem('selectedBank', selectedBank);
    }, [selectedBank]);

    const [fileValidStatus, setFileValidStatus] = useState(undefined)
    const [currentStep, setCurrentStep] = useState({ activeStep: 0, status: "" }); // at first no file is selected, stepper is at the first step
    // eslint-disable-next-line no-unused-vars
    const [orderbookID, setOrderbookID] = useState([]); // orderbookID is set by UploadButton but not read directly in this component
    const [orderBooks, setOrderBooks] = useState([])
    const makeFileKey = (f) => `${f?.name ?? ""}::${f?.size ?? ""}::${f?.lastModified ?? ""}`;
    const guessMimeType = (name, fallbackType) => {
        const lower = (name || "").toString().toLowerCase();
        if (fallbackType) return fallbackType;
        if (lower.endsWith(".pdf")) return "application/pdf";
        if (lower.endsWith(".csv")) return "text/csv";
        return "";
    };
    const toExtFile = (f) => {
        if (!f) return null;

        // Already an extFile from @files-ui/react
        if (typeof f === "object" && f.file) {
            // Ensure type is present for proper PDF icon/preview handling
            const fileType = f.type || guessMimeType(f.name, f.file?.type);
            return { ...f, type: fileType };
        }

        // Raw File from FileList or drag/drop events
        if (typeof f === "object" && (f instanceof File || typeof f.name === "string")) {
            const id = `f_${Date.now()}_${Math.random().toString(16).slice(2)}`;
            return {
                id,
                file: f,
                name: f.name,
                size: f.size,
                lastModified: f.lastModified,
                type: guessMimeType(f.name, f.type),
                valid: undefined,
                duplicate: false
            };
        }

        return null;
    };

    const getFileKind = (f) => {
        const name = (f?.name || '').toString().toLowerCase();
        const type = (f?.file?.type || f?.type || '').toString().toLowerCase();
        if (name.endsWith('.pdf') || type === 'application/pdf' || type.endsWith('/pdf')) return 'pdf';
        if (name.endsWith('.csv') || type === 'text/csv' || type.endsWith('/csv')) return 'csv';
        return 'file';
    };

    const samplePdfUrl = `${window.location.origin || ''}/wp-content/plugins/react-app/sample-files/sample-orderbook.pdf`;

    const addSamplePdfToDropzone = async () => {
        if (sampleBusy) return;
        setSampleError('');
        setSampleBusy(true);
        try {
            const response = await fetch(samplePdfUrl, { cache: 'no-store' });
            if (!response.ok) {
                throw new Error(`Sample PDF unavailable (HTTP ${response.status}).`);
            }
            const blob = await response.blob();
            const sampleFile = new File([blob], 'sample-orderbook.pdf', {
                type: blob.type || 'application/pdf',
                lastModified: Date.now(),
            });
            const ext = toExtFile(sampleFile);
            if (!ext) {
                throw new Error('Could not prepare sample PDF.');
            }

            setCurrentStep({ activeStep: 0, status: "" });
            setFileValidStatus(undefined);
            setFiles((prev) => {
                const normalizedPrev = (Array.isArray(prev) ? prev : []).map(toExtFile).filter(Boolean);
                const candidate = [...normalizedPrev, ext];
                const seen = new Set();
                const unique = [];
                for (const f of candidate) {
                    const k = makeFileKey(f);
                    if (seen.has(k)) continue;
                    seen.add(k);
                    unique.push(f);
                }
                return unique;
            });
        } catch (e) {
            setSampleError(e?.message || 'Failed to load sample PDF.');
        } finally {
            setSampleBusy(false);
        }
    };

    const handleDeleteFile = (idOrKey) => {
        setFiles(prev => prev.filter((f) => (f.id || makeFileKey(f)) !== idOrKey));
        setFileValidStatus(undefined);
        setCurrentStep({ activeStep: 0, status: "" });
    };
    const normalizeIncomingFiles = (incomingFiles) => {
        if (Array.isArray(incomingFiles)) return incomingFiles;
        if (!incomingFiles) return [];

        // Some wrappers pass an event instead of the file list
        if (incomingFiles?.target?.files) {
            try {
                return Array.from(incomingFiles.target.files);
            } catch (e) {
                return [];
            }
        }
        if (incomingFiles?.dataTransfer?.files) {
            try {
                return Array.from(incomingFiles.dataTransfer.files);
            } catch (e) {
                return [];
            }
        }

        // Some environments pass FileList or array-like objects
        if (typeof incomingFiles === "object" && typeof incomingFiles.length === "number") {
            try {
                return Array.from(incomingFiles);
            } catch (e) {
                return [];
            }
        }

        // Some wrappers pass { files: [...] }
        if (typeof incomingFiles === "object" && Array.isArray(incomingFiles.files)) {
            return incomingFiles.files;
        }

        return [];
    };
    useEffect(() => {
        let cancelled = false;
        let retryTimer = null;

        const tryFetchBanks = async () => {
            if (cancelled) return;
            setBanksLoadState({ status: "loading", message: "" });
            try {
                const res = await fetch(`${API}/banks/`, { cache: "no-store" });
                const contentType = String(res.headers.get("content-type") || "").toLowerCase();

                if (res.status === 429) {
                    // Respect server hint to avoid hammering local dev API.
                    const retryAfterRaw = res.headers.get("retry-after");
                    const retryAfterSec = Number.parseInt(String(retryAfterRaw || ""), 10);
                    const delayMs = Number.isFinite(retryAfterSec) && retryAfterSec > 0
                        ? Math.min(retryAfterSec * 1000, 5 * 60 * 1000)
                        : 30 * 1000;

                    if (!cancelled) {
                        setBanks([]);
                        setBanksLoadState({
                            status: "issue",
                            message: `API throttled (429). Retrying in ${Math.ceil(delayMs / 1000)}s…`,
                        });
                        retryTimer = setTimeout(tryFetchBanks, delayMs);
                    }
                    return;
                }

                if (!res.ok) {
                    if (!cancelled) {
                        setBanks([]);
                        setBanksLoadState({ status: "issue", message: `Failed to load banks (HTTP ${res.status}).` });
                    }
                    return;
                }

                if (contentType.indexOf("application/json") === -1) {
                    if (!cancelled) {
                        setBanks([]);
                        setBanksLoadState({ status: "issue", message: "Banks response was not JSON." });
                    }
                    return;
                }

                const data = await res.json();
                if (!cancelled) {
                    setBanks(Array.isArray(data) ? data : []);
                    setBanksLoadState(Array.isArray(data) ? { status: "ready", message: "" } : { status: "issue", message: "Banks payload was not a list." });
                }
            } catch (e) {
                if (!cancelled) {
                    setBanks([]);
                    setBanksLoadState({ status: "issue", message: "Network error while loading banks." });
                }
            }
        };

        tryFetchBanks();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, []);

    useEffect(() => {
        // Language (safe): German default, English only via ?lang=en
        setIsGerman(computeIsGermanDefault());
    }, []);

    useEffect(() => {
        let active = true;
        const lang = getCurrentLangCode();
        const includeTestFlag = isUiTextTestMode();
        const mode = includeTestFlag ? 'draft' : 'approved';
        const url = getUiTextRestUrl(lang, 'upload', mode, includeTestFlag);

        fetch(url, { cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!active) return;
                if (!data || !data.success || !data.texts || typeof data.texts !== 'object') {
                    setUiTexts({});
                    return;
                }
                setUiTexts(data.texts);
            })
            .catch(() => {
                if (active) setUiTexts({});
            });

        return () => {
            active = false;
        };
    }, [isGerman]);

    // Preserve in-tab progress across refresh (e.g. billing page reload).
    useEffect(() => {
        try {
            const savedStep = Number(sessionStorage.getItem('ryp_resume_step') || '0');
            const savedStatus = String(sessionStorage.getItem('ryp_resume_status') || '');
            if (savedStep >= 1 && savedStep <= 5) {
                setCurrentStep({ activeStep: savedStep, status: savedStatus });
            }
        } catch (e) {
            // Ignore storage issues and keep default step.
        }
    }, []);

    // Keep step state in session storage for same-tab refresh recovery.
    useEffect(() => {
        try {
            if (currentStep.activeStep >= 1) {
                sessionStorage.setItem('ryp_resume_step', String(currentStep.activeStep));
                sessionStorage.setItem('ryp_resume_status', String(currentStep.status || ''));
            } else {
                sessionStorage.removeItem('ryp_resume_step');
                sessionStorage.removeItem('ryp_resume_status');
            }
        } catch (e) {
            // Ignore storage issues.
        }
    }, [currentStep]);

    // On hard refresh, restore only when explicitly requested via URL.
    // Default behavior stays at step 0 to avoid unexpected jumps after login.
    // Localhost-only extension: allow paid status to reopen Billing/Results during Payrexx local testing.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const resumeRequested = params.get('resume_checkout') === '1';
            const payrexxParam = String(params.get('payrexx') || '').toLowerCase();
            const host = String(window.location.hostname || '').toLowerCase();
            const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
            let sessionExpected = '';
            let returnAtRaw = '';
            try {
                sessionExpected = String(sessionStorage.getItem('ryp_payrexx_return_expected') || '');
                returnAtRaw = String(sessionStorage.getItem('ryp_payrexx_return_at') || '');
            } catch (e) {
                sessionExpected = '';
                returnAtRaw = '';
            }
            const returnAtMs = parseInt(returnAtRaw, 10);
            const withinCheckoutSession =
                Number.isFinite(returnAtMs) &&
                returnAtMs > 0 &&
                Date.now() - returnAtMs < 3 * 60 * 60 * 1000 &&
                Date.now() >= returnAtMs - 60 * 1000;

            if (isLocalHost && (payrexxParam === 'failed' || payrexxParam === 'cancelled')) {
                try {
                    sessionStorage.removeItem('ryp_payrexx_return_expected');
                    sessionStorage.removeItem('ryp_payrexx_return_at');
                } catch (e) {
                    /* ignore */
                }
            }

            // Localhost: return URL sometimes has no query args; recover Billing if checkout was recent and method was Payrexx.
            let paymentMethodStored = '';
            try {
                const pd = JSON.parse(localStorage.getItem('paymentData') || '{}');
                paymentMethodStored = String(pd.paymentMethod || '').toLowerCase();
            } catch (e) {
                paymentMethodStored = '';
            }
            const localBareHomeResume = isLocalHost
                && sessionExpected === '1'
                && paymentMethodStored === 'payrexx'
                && withinCheckoutSession
                && !resumeRequested
                && payrexxParam === '';
            // Payrexx/PayPal may drop resume_checkout=1; success/cancel URLs still carry payrexx= on many setups.
            const localPayrexxSuccessUrl = isLocalHost && payrexxParam === 'success';

            if (!resumeRequested && !localPayrexxSuccessUrl && !localBareHomeResume) return;

            const paymentDataStr = localStorage.getItem('paymentData');
            const lastOrderId = localStorage.getItem('lastOrderId');
            if (!paymentDataStr || !lastOrderId) return;

            const paymentData = JSON.parse(paymentDataStr || '{}');
            const restoredStatus = String(paymentData?.status || '').toLowerCase();
            if (localPayrexxSuccessUrl && !resumeRequested) {
                const pm = String(paymentData.paymentMethod || '').toLowerCase();
                if (pm !== 'payrexx') return;
            }
            const resumableStatuses = new Set(['awaiting_payment', 'pending_confirmation', 'pending']);
            if (isLocalHost) {
                resumableStatuses.add('paid');
            }
            if (!resumableStatuses.has(restoredStatus)) return;

            const timestampMs = paymentData?.timestamp ? new Date(paymentData.timestamp).getTime() : NaN;
            // Localhost Payrexx success or bare-home recovery: show Billing for dev (webhook may lag).
            if (!localPayrexxSuccessUrl && !localBareHomeResume) {
                if (!Number.isFinite(timestampMs)) return;
                if ((Date.now() - timestampMs) > (24 * 60 * 60 * 1000)) return;
            }

            if (localPayrexxSuccessUrl || localBareHomeResume) {
                try {
                    sessionStorage.removeItem('ryp_payrexx_return_expected');
                    sessionStorage.removeItem('ryp_payrexx_return_at');
                } catch (e) {
                    /* ignore */
                }
            }

            // Keep paid orders on Billing first so users can still proceed to Results via normal UI.
            setCurrentStep({ activeStep: 4, status: restoredStatus });
        } catch (e) {
            // Ignore malformed localStorage and keep default start step.
        }
    }, []);

    const updateFiles = (incomingFiles) => {
        // Some Dropzone implementations emit ONLY the newly added files when selecting one-by-one.
        // To ensure the counter and UI reflect all chosen files, we merge when there's no overlap.
        const incoming = normalizeIncomingFiles(incomingFiles).map(toExtFile).filter(Boolean);

        setCurrentStep({ activeStep: 0, status: "" });
        setFileValidStatus(undefined);

        setFiles((prev) => {
            // Delete-all action
            if (incoming.length === 0) return [];

            const prevNormalized = (Array.isArray(prev) ? prev : []).map(toExtFile).filter(Boolean);
            const prevKeys = new Set(prevNormalized.map(makeFileKey));
            const hasOverlap = incoming.some((f) => prevKeys.has(makeFileKey(f)));

            const merged = hasOverlap ? incoming : [...prevNormalized, ...incoming];

            // De-dupe (by name+size+lastModified) and cap to MAX_FILES
            const seen = new Set();
            const unique = [];
            for (const f of merged) {
                const k = makeFileKey(f);
                if (seen.has(k)) continue;
                seen.add(k);
                unique.push(f);
                if (unique.length >= MAX_FILES) break;
            }

            return unique;
        });
    };

    // whenever the files state updates, use an effect to update immidiately the Files
    useEffect(() => { }, [files]);

    const [selected, setSelected] = useState([]);

    // When user clicks "Back" from Preview, reset fileValidStatus so upload UI re-appears
    useEffect(() => {
        if (currentStep.activeStep === 0 && currentStep.status === 'back-from-preview') {
            setFileValidStatus(undefined);
        }
    }, [currentStep]);

    const activeComponent = () => {
        if (currentStep.activeStep === 1) {
            return <Preview orderBooks={orderBooks} setOrderBooks={setOrderBooks} setCurrentStep={setCurrentStep} selected={selected} setSelected={setSelected} isGerman={isGerman}></Preview>;
        }
        if (currentStep.activeStep === 2) {
            return <ProductChoice setCurrentStep={setCurrentStep} />;
        }
        if (currentStep.activeStep === 3) {
            return <ClientDetailsPayment setCurrentStep={setCurrentStep} />;
        }
        if (currentStep.activeStep === 4) {
            return <BillingPage setCurrentStep={setCurrentStep} />;
        }
        if (currentStep.activeStep === 5) {
            return <ResultsPage setCurrentStep={setCurrentStep} />;
        }
        return null;
    };

    // Local-only "Payrexx Checkout" simulator screen:
    // the backend creates an order, then returns a checkout_url that includes `payrexx_checkout_sim=1`.
    // In that case we render a lightweight simulator UI and keep the real checkout flow untouched.
    try {
        const params = new URLSearchParams(window.location.search || '');
        const payrexxSimRequested = params.get('payrexx_checkout_sim') === '1';
        if (payrexxSimRequested) {
            return <PayrexxCheckoutSimulator />;
        }
    } catch (e) {
        // Ignore parsing errors.
    }

    return (

        <div>
            <style>{`
                .agreement-text-wrapper span,
                .agreement-text-wrapper .MuiFormControlLabel-label,
                .agreement-text-wrapper .MuiFormControlLabel-label span {
                    font-size: 11px !important;
                    max-width: 400px !important;
                    line-height: 1.3 !important;
                    display: inline-block !important;
                    word-wrap: break-word !important;
                }
                .agreement-text-wrapper {
                    width: 100% !important;
                    max-width: 100% !important;
                }
                .agreement-text-wrapper .MuiFormControlLabel-root {
                    margin: 0 !important;
                    width: auto !important;
                    max-width: 100% !important;
                    justify-content: flex-end !important;
                    margin-left: auto !important;
                }
            `}</style>
            {fileValidStatus ? (
                <AlertMessage
                    timeout={2000}
                    message={getUploadText(uiTexts, 'upload.alert.upload_success', isPolish ? "Twoja historia została przesłana" : (isGerman ? "Ihr Orderbuch wurde hochgeladen" : "Your history was uploaded"))}
                />
            ) : ''}
            <div className="row justify-content-center">
                {/* the Stepper component receive the status from its parent */}
                <UploadProgress currentStep={currentStep} isGerman={isGerman} uiTexts={uiTexts} />

                {(currentStep.activeStep === 0 && !fileValidStatus) ?
                    <div className="col-12">
                        <BankSelector banks={banks} selectedBank={selectedBank} setSelectedBank={setSelectedBank} uiTexts={uiTexts} />
                        {banksLoadState.status === "issue" && banksLoadState.message ? (
                            <div className="alert alert-warning mt-2" role="alert" style={{ marginBottom: 0 }}>
                                {banksLoadState.message}
                            </div>
                        ) : null}
                        <div className="w-100 text-center">
                            {/* Single counter OUTSIDE the drag area */}
                            {/* Mark as notranslate so browser auto-translate won't break React updates */}
                            <Box
                                className="notranslate"
                                translate="no"
                                sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}
                            >
                                <Typography className="notranslate" translate="no" sx={{ fontWeight: 700, color: '#1e90ff' }}>
                                    {getUploadText(uiTexts, 'upload.counter.files', isPolish ? 'Pliki' : (isGerman ? 'Dateien' : 'Files'))} {files.length}/{MAX_FILES}
                                </Typography>
                            </Box>
                            <Dropzone
                                accept=".pdf, .csv"
                                header={false}
                                footer={false}
                                label={
                                    <div style={{
                                        color: "#1e90ff", fontWeight: "bold", fontSize: "1.05rem", position: "relative",
                                        zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8
                                    }}>
                                        <PictureAsPdfIcon sx={{ fontSize: 48, color: '#d32f2f' }} />
                                        <div>
                                            {getUploadText(uiTexts, 'upload.dropzone.label', isPolish ? 'Przeciągnij i upuść pliki tutaj lub kliknij, aby przesłać' : (isGerman ? 'Dateien hierher ziehen oder klicken zum Hochladen' : 'Drag & Drop files here or click to upload'))}
                                        </div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e90ff" }}>
                                            {getUploadText(uiTexts, 'upload.dropzone.file_types', 'PDF / CSV')}
                                        </div>
                                    </div>
                                }
                                maxFiles={MAX_FILES} // adjust the max number of files that user can upload
                                value={files}
                                // onChange is called when a file is selected or a delete all files in the top right corner is clicked
                                onChange={updateFiles}
                                style={{
                                    border: "2px dashed #1e90ff",
                                    borderRadius: "10px",
                                    backgroundColor: "#f8f9fa",
                                    padding: "30px",
                                    color: "#1e90ff",
                                    textAlign: "center",
                                    transition: "background-color 0.3s ease",
                                }}
                            >
                                {/* File list with local icons (doesn't depend on external image URLs / CSP) */}
                                {files.length > 0 && (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, textAlign: "left" }}>
                                        {files.map((file) => {
                                            const kind = getFileKind(file);
                                            const key = file.id || makeFileKey(file);
                                            const Icon = kind === 'pdf'
                                                ? PictureAsPdfIcon
                                                : kind === 'csv'
                                                    ? TableChartIcon
                                                    : InsertDriveFileIcon;
                                            const iconColor = kind === 'pdf' ? '#d32f2f' : '#1e90ff';

                                            return (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        px: 1.25,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        border: "1px solid rgba(30,144,255,0.25)",
                                                        backgroundColor: "#fff",
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                        <Icon sx={{ color: iconColor, flex: "0 0 auto" }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, wordBreak: "break-word" }}>
                                                            {file.name}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Remove file"
                                                        onClick={() => handleDeleteFile(key)}
                                                        sx={{ flex: "0 0 auto" }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Dropzone>
                            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#1e90ff', fontWeight: 600 }}>
                                    Sample file: PDF
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <a
                                        href={samplePdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                        >
                                            Download sample PDF
                                        </button>
                                    </a>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={addSamplePdfToDropzone}
                                        disabled={sampleBusy}
                                    >
                                        {sampleBusy ? 'Adding sample...' : 'Use sample PDF'}
                                    </button>
                                </Box>
                            </Box>
                            {sampleError ? (
                                <div className="alert alert-warning mt-2 mb-0" role="alert">
                                    {sampleError}
                                </div>
                            ) : null}
                            <div className="w-100 mt-3 agreement-text-wrapper" style={{ width: '100%', maxWidth: '100%' }}>
                                <div className="d-flex justify-content-end align-items-center mb-2" style={{ width: '100%', maxWidth: '100%' }}>
                                    <FormControlLabel
                                        style={{ width: 'auto', maxWidth: '100%', justifyContent: 'flex-end', margin: 0 }}
                                        control={
                                            <Checkbox
                                                checked={agreementAccepted}
                                                onChange={(e) => setAgreementAccepted(e.target.checked)}
                                                size="small"
                                                sx={{ 
                                                    '& .MuiSvgIcon-root': { 
                                                        fontSize: '14px !important',
                                                        width: '14px',
                                                        height: '14px'
                                                    } 
                                                }}
                                            />
                                        }
                                        label={
                                            <span style={{ 
                                                fontSize: '11px !important',
                                                maxWidth: '400px !important',
                                                marginLeft: '4px',
                                                lineHeight: '1.3 !important',
                                                display: 'inline-block !important',
                                                wordWrap: 'break-word !important'
                                            }}>
                                                {isGerman 
                                                    ? getUploadText(uiTexts, 'upload.consent.text', "Durch Klicken auf die Upload-Schaltfläche stimmen Sie zu, dass wir Ihre Daten verarbeiten, um Ihnen Ergebnisse zu liefern.")
                                                    : getUploadText(uiTexts, 'upload.consent.text', "By clicking Next, you agree to the processing of your data to provide your results")
                                                }
                                            </span>
                                        }
                                        sx={{ 
                                            alignItems: 'center',
                                            m: 0,
                                            '& .MuiFormControlLabel-label': {
                                                mt: 0,
                                                fontSize: '11px !important'
                                            }
                                        }}
                                    />
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2" style={{ width: '100%', marginTop: '8px' }}>
                                    <SystemReadinessBadge />
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flex: '0 0 auto' }}>
                                        <UploadButton filesToUpload={files} selectedBank={selectedBank} setFiles={setFiles}
                                            setFileValidStatus={setFileValidStatus} setCurrentStep={setCurrentStep}
                                            setOrderbookID={setOrderbookID} setOrderBooks={setOrderBooks}
                                            isGerman={isGerman}
                                            agreementAccepted={agreementAccepted}
                                            uiTexts={uiTexts}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> : activeComponent()}


            </div>
        </div>
    );
}

export default UploadFile;
